import React from 'react';
import { useTable, usePagination, useRowSelect } from 'react-table';
import {
  FaEdit,
  FaHistory,
  FaChevronLeft,
  FaChevronRight,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaInfoCircle,
  FaShareAlt,
  FaBan,
} from 'react-icons/fa';
import InlineEditableCell from './InlineEditableCell';

// Removed parseCustomDateTime helper function as it's no longer needed here
// Checkbox component for row selection
const IndeterminateCheckbox = React.forwardRef(({ indeterminate, ...rest }, ref) => {
  const defaultRef = React.useRef();
  const resolvedRef = ref || defaultRef;

  React.useEffect(() => {
    resolvedRef.current.indeterminate = indeterminate;
  }, [resolvedRef, indeterminate]);

  return (
    <input
      type="checkbox"
      ref={resolvedRef}
      {...rest}
      className="form-checkbox h-4 w-4 text-primary-600 transition duration-150 ease-in-out"
    />
  );
});

// Reusable component for table cells needing specific formatting or actions
const ActionCell = ({
  row,
  onViewHistory,
  onViewTransactionDetails,
  onShareTransaction,
  onExcludeTicket,
}) => {
  const isExcluded = row.original?.IsExcluded === 1 || row.original?.IsExcluded === true;
  return (
    <div className="flex items-center gap-1">
      {onViewHistory && row.original.TicketUID && (
        <button
          onClick={() => onViewHistory(row.original.TicketUID)}
          className="text-slate-400 hover:text-primary-600 p-1 rounded hover:bg-primary-50 transition-colors"
          title="View History"
        >
          <FaHistory className="w-3 h-3" />
        </button>
      )}
      {onViewTransactionDetails && row.original && (
        <button
          onClick={() => onViewTransactionDetails(row.original)}
          className="text-slate-400 hover:text-emerald-600 p-1 rounded hover:bg-emerald-50 transition-colors"
          title="View Transaction Details"
        >
          <FaInfoCircle className="w-3 h-3" />
        </button>
      )}
      {onShareTransaction && row.original && (
        <button
          onClick={() => onShareTransaction(row.original)}
          className="text-slate-400 hover:text-primary-600 p-1 rounded hover:bg-primary-50 transition-colors"
          title="Share Transaction"
        >
          <FaShareAlt className="w-3 h-3" />
        </button>
      )}
      {onExcludeTicket && row.original?.TicketUID && (
        <button
          onClick={() => onExcludeTicket(row.original)}
          className={`p-1 rounded transition-colors ${
            isExcluded
              ? 'text-red-500 hover:text-emerald-600 hover:bg-emerald-50'
              : 'text-slate-400 hover:text-red-600 hover:bg-red-50'
          }`}
          title={isExcluded ? 'Restore excluded ticket' : 'Mark as duplicate / exclude'}
        >
          <FaBan className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

const DataTable = ({
  columns,
  data,
  pagination, // { page, limit, totalCount, totalPages }
  onPageChange,
  onViewHistory,
  onViewTransactionDetails,
  onShareTransaction,
  onExcludeTicket,
  loading,
  onSelectedRowsChange,
  selectedRows,
  onBulkAmend,
  onBulkExclude,
  highlightTicketUID,
  editMode,
  pendingEdits,
  onCellEdit,
  readOnly = false,
}) => {
  // Map TicketUID → <tr> element, used to scroll find-matches into view
  const rowRefs = React.useRef({});

  React.useEffect(() => {
    if (!highlightTicketUID) return;
    const el = rowRefs.current[highlightTicketUID];
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightTicketUID, data]);

  // Removed performValidation function

  // Simplified ValidationCell to read pre-calculated status from backend
  const ValidationCell = ({ row }) => {
    // Get the validation status directly from the row data provided by the backend
    const status = row.original?.validationStatus || 'NA'; // Default to 'NA' if not present

    let statusText = 'N/A';
    let textColor = 'text-gray-400';
    let titleText = 'Validation not applicable or not performed.'; // Default tooltip

    switch (status) {
      case 'Matched':
        statusText = 'Matched';
        textColor = 'text-green-500';
        titleText = 'Validation Passed: Matching Dispatch found.';
        break;
      case 'NotFound':
        statusText = 'Not Found';
        textColor = 'text-red-500';
        titleText = 'Validation Error: Matching Dispatch ticket not found.';
        break;
      case 'RegMismatch':
        statusText = 'Reg No Mismatch';
        textColor = 'text-red-500';
        titleText = 'Validation Error: Registration number mismatch.';
        break;
      case 'TimeError':
        statusText = 'Time Sequence Error';
        textColor = 'text-red-500';
        titleText = 'Validation Error: Receipt timestamp is not after Dispatch timestamp.';
        break;
      case 'InvalidMatchDate':
        statusText = 'Invalid Match Date';
        textColor = 'text-orange-500';
        titleText =
          'Validation Warning: Could not parse the timestamp of the matched Dispatch ticket.';
        break;
      case 'MatchTypeMismatch':
        statusText = 'Match Type Error';
        textColor = 'text-red-500';
        titleText = 'Validation Error: The matched ticket is not a Dispatch type.';
        break;
      // 'InvalidDate' (for current row) is less likely needed now as backend handles it, but could be added if backend sends it
      // 'FetchError' is no longer applicable here as fetch happens server-side
      case 'NA':
      default:
        // Keep default N/A style
        break;
    }

    // No onClick handler needed anymore as details are not fetched client-side
    // We use the 'title' attribute for basic hover info instead.
    return (
      <span className={`${textColor}`} title={titleText}>
        {statusText}
      </span>
    );
  };

  // Add the validation column dynamically
  // The accessor is simple, the Cell component handles the async logic
  const tableColumns = React.useMemo(
    () => [
      // Add Row Number column
      {
        Header: '#',
        id: 'rowNumber', // Unique ID for the column
        Cell: ({ row, state }) => {
          // Access state for pageIndex and pageSize
          // Calculate the row number based on pagination
          const pageIndex = state.pageIndex;
          const pageSize = state.pageSize;
          return pageIndex * pageSize + row.index + 1;
        },
        // Optional: Style the column if needed
        className: 'text-center text-xs font-semibold text-gray-500', // Example styling
        width: 50, // Example fixed width
      },
      ...columns,
      {
        Header: 'Delivery Validation',
        id: 'deliveryValidation', // Give it a unique ID
        // Cell now receives the whole row object
        Cell: ({ row }) => <ValidationCell row={row} />,
      },
    ],
    [columns, pagination.page, pagination.limit]
  ); // Add pagination dependencies if needed for calculation context, though state is preferred

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page, // Instead of using 'rows', we use 'page' which has only the rows for the current page
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state, // Access the full state object which includes pageIndex and pageSize
    selectedFlatRows, // Use selectedFlatRows to get original data for selected rows
  } = useTable(
    {
      columns: tableColumns, // Use the modified columns array
      data,
      initialState: { pageIndex: pagination.page - 1, pageSize: pagination.limit }, // Sync with external state
      manualPagination: true, // Tell react-table we're doing pagination server-side
      pageCount: pagination.totalPages, // Total pages from server
      // Pass the full state down to the Cell renderer for row number calculation
      // This is implicitly handled by react-table, but ensure the Cell function receives props correctly
    },
    usePagination,
    useRowSelect, // Use the row selection hook
    // Add selection column
    (hooks) => {
      hooks.visibleColumns.push((allColumns) => {
        if (readOnly) return allColumns;
        return [
          {
            id: 'selection',
            Header: ({ getToggleAllPageRowsSelectedProps }) => (
              <div>
                <IndeterminateCheckbox {...getToggleAllPageRowsSelectedProps()} />
              </div>
            ),
            Cell: ({ row }) => (
              <div>
                <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
              </div>
            ),
          },
          ...allColumns,
        ];
      });
    }
  );

  // Report selected rows change to parent using selectedFlatRows
  React.useEffect(() => {
    if (onSelectedRowsChange) {
      const selectedOriginalData = selectedFlatRows.map((d) => d.original);
      onSelectedRowsChange(selectedOriginalData);
    }
  }, [selectedFlatRows, onSelectedRowsChange]); // Depend on selectedFlatRows

  // Update react-table page index if external page changes
  React.useEffect(() => {
    gotoPage(pagination.page - 1);
  }, [pagination.page, gotoPage]);

  // Update react-table page size if external limit changes
  React.useEffect(() => {
    setPageSize(pagination.limit);
  }, [pagination.limit, setPageSize]);

  // Handle external page change request
  const handleGoToPage = (page) => {
    onPageChange(page + 1); // Parent expects 1-based index
  };
  const handleNextPage = () => onPageChange(pagination.page + 1);
  const handlePreviousPage = () => onPageChange(pagination.page - 1);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-400">
        <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
            fill="none"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
        <span className="text-sm">Loading data...</span>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-slate-500">
        <svg
          className="w-10 h-10 mb-2 text-slate-300"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
          />
        </svg>
        <span className="text-sm font-medium">No data available</span>
        <span className="text-xs mt-0.5">Apply filters and search to load tickets</span>
      </div>
    );
  }

  return (
    <div>
      {/* Bulk Action Bar - outside table scroll so it stays visible */}
      {!readOnly && (onBulkAmend || onBulkExclude) && (
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-slate-50/50">
          <div className="flex items-center gap-2">
            {onBulkAmend && (
              <button
                onClick={onBulkAmend}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                  selectedRows && selectedRows.length > 0
                    ? 'bg-violet-600 text-white hover:bg-violet-700 shadow-sm'
                    : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                }`}
                disabled={!selectedRows || selectedRows.length === 0}
              >
                <FaEdit className="w-3 h-3" />
                Bulk Amend
                {selectedRows && selectedRows.length > 0 ? ` (${selectedRows.length})` : ''}
              </button>
            )}
            {onBulkExclude && (
              <button
                onClick={onBulkExclude}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                  selectedRows && selectedRows.length > 0
                    ? 'bg-red-600 text-white hover:bg-red-700 shadow-sm'
                    : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                }`}
                disabled={!selectedRows || selectedRows.length === 0}
              >
                <FaBan className="w-3 h-3" />
                Bulk Exclude
                {selectedRows && selectedRows.length > 0 ? ` (${selectedRows.length})` : ''}
              </button>
            )}
          </div>
          <span className="text-xs text-slate-400">
            {selectedRows && selectedRows.length > 0
              ? `${selectedRows.length} row${selectedRows.length !== 1 ? 's' : ''} selected`
              : 'Click rows to select'}
          </span>
        </div>
      )}

      {/* Scrollable table container */}
      <div className="overflow-x-auto">
        <table
          {...getTableProps()}
          className="w-full divide-y divide-slate-200"
          style={{ minWidth: '800px' }}
        >
          <thead>
            {headerGroups.map((headerGroup, headerGroupIndex) => (
              <tr key={headerGroupIndex} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, columnIndex) => (
                  <th
                    key={columnIndex}
                    {...column.getHeaderProps()}
                    style={{ width: column.width ? `${column.width}px` : undefined }}
                    className={`px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider border-b border-slate-200 ${
                      !readOnly &&
                      editMode &&
                      InlineEditableCell.isEditable(column.id || column.accessor)
                        ? 'text-primary-600 bg-primary-50/50'
                        : 'text-slate-500 bg-slate-50'
                    } ${column.className || ''}`}
                  >
                    {column.render('Header')}
                  </th>
                ))}
                {(onViewHistory ||
                  onViewTransactionDetails ||
                  onShareTransaction ||
                  onExcludeTicket) && (
                  <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider bg-slate-50 border-b border-slate-200 w-20" />
                )}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()} className="divide-y divide-slate-100">
            {page.map((row, rowIndex) => {
              prepareRow(row);
              const actualRowIndex = (pagination.page - 1) * pagination.limit + rowIndex;
              const isEvenRow = actualRowIndex % 2 === 0;
              const rowTicketUID = row.original?.TicketUID;
              const isFindMatch = highlightTicketUID && rowTicketUID === highlightTicketUID;

              const isRowExcluded =
                row.original?.IsExcluded === 1 || row.original?.IsExcluded === true;
              let rowBgClass;
              if (isFindMatch) {
                rowBgClass = 'bg-amber-50 ring-2 ring-inset ring-amber-300';
              } else if (row.isSelected) {
                rowBgClass = 'bg-primary-50';
              } else if (isRowExcluded) {
                rowBgClass = 'bg-red-50/40 border-l-2 border-red-400';
              } else {
                rowBgClass = isEvenRow ? 'bg-surface' : 'bg-slate-50/50';
              }

              const handleRowClick = (e) => {
                if (readOnly) return;
                if (
                  e.target.closest('button') ||
                  e.target.type === 'checkbox' ||
                  e.target.closest('input')
                ) {
                  return;
                }
                if (editMode) return;
                row.toggleRowSelected();
              };

              const rowProps = row.getRowProps();

              return (
                <tr
                  key={rowIndex}
                  {...rowProps}
                  ref={(el) => {
                    if (rowTicketUID) {
                      if (el) rowRefs.current[rowTicketUID] = el;
                      else delete rowRefs.current[rowTicketUID];
                    }
                  }}
                  className={`${rowBgClass} hover:bg-primary-50/60 cursor-pointer transition-colors${isRowExcluded ? ' opacity-50' : ''}`}
                  onClick={handleRowClick}
                >
                  {row.cells.map((cell, cellIndex) => {
                    const accessor = cell.column.id || cell.column.accessor;
                    const shouldInlineEdit =
                      !readOnly &&
                      editMode &&
                      onCellEdit &&
                      InlineEditableCell.isEditable(accessor);
                    return (
                      <td
                        key={cellIndex}
                        {...cell.getCellProps()}
                        className={`px-3 py-1.5 whitespace-nowrap text-xs text-slate-700 relative ${cell.column.className || ''}`}
                      >
                        {shouldInlineEdit ? (
                          <InlineEditableCell
                            value={cell.value}
                            row={row}
                            column={cell.column}
                            editMode={editMode}
                            pendingEdits={pendingEdits}
                            onCellEdit={onCellEdit}
                          />
                        ) : (
                          (() => {
                            const editKey = `${row.original?.TicketUID}::${accessor}`;
                            const hasPending =
                              !readOnly && pendingEdits && pendingEdits.has(editKey);
                            if (hasPending && !editMode) {
                              return (
                                <InlineEditableCell
                                  value={cell.value}
                                  row={row}
                                  column={cell.column}
                                  editMode={false}
                                  pendingEdits={pendingEdits}
                                  onCellEdit={onCellEdit}
                                />
                              );
                            }
                            return cell.render('Cell', { state });
                          })()
                        )}
                      </td>
                    );
                  })}
                  {(onViewHistory ||
                    onViewTransactionDetails ||
                    onShareTransaction ||
                    onExcludeTicket) && (
                    <td
                      key="actions"
                      className="px-3 py-1.5 whitespace-nowrap"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ActionCell
                        row={row}
                        onViewHistory={onViewHistory}
                        onViewTransactionDetails={onViewTransactionDetails}
                        onShareTransaction={onShareTransaction}
                        onExcludeTicket={onExcludeTicket}
                      />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls - compact footer bar */}
      {pagination.totalPages > 0 && (
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-200 bg-slate-50/50">
          <p className="text-xs text-slate-500">
            Page <span className="font-semibold text-slate-700">{pagination.page}</span> of{' '}
            <span className="font-semibold text-slate-700">{pagination.totalPages}</span>
            <span className="text-slate-300 mx-1.5">|</span>
            <span className="font-semibold text-slate-700">
              {pagination.totalCount.toLocaleString()}
            </span>{' '}
            results
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => handleGoToPage(0)}
              disabled={pagination.page <= 1}
              className="inline-flex items-center justify-center w-7 h-7 rounded border border-slate-300 bg-surface text-slate-500 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <FaAngleDoubleLeft className="w-3 h-3" />
            </button>
            <button
              onClick={handlePreviousPage}
              disabled={pagination.page <= 1}
              className="inline-flex items-center justify-center w-7 h-7 rounded border border-slate-300 bg-surface text-slate-500 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <FaChevronLeft className="w-2.5 h-2.5" />
            </button>
            <span className="inline-flex items-center justify-center px-3 h-7 text-xs font-medium text-slate-700 bg-surface border border-slate-300 rounded select-none">
              {pagination.page}
            </span>
            <button
              onClick={handleNextPage}
              disabled={pagination.page >= pagination.totalPages}
              className="inline-flex items-center justify-center w-7 h-7 rounded border border-slate-300 bg-surface text-slate-500 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <FaChevronRight className="w-2.5 h-2.5" />
            </button>
            <button
              onClick={() => handleGoToPage(pagination.totalPages - 1)}
              disabled={pagination.page >= pagination.totalPages}
              className="inline-flex items-center justify-center w-7 h-7 rounded border border-slate-300 bg-surface text-slate-500 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <FaAngleDoubleRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
